import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/RecallBatch.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFilter,
  faSearch,
  faExclamationTriangle,
  faRotateLeft,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";

/* ── tiny recalled pill ── */
function RecalledPill() {
  return (
    <span className="recalled-pill">
      <FontAwesomeIcon icon={faRotateLeft} style={{ fontSize: 10 }} />
      Recalled
    </span>
  );
}

function RecallBatch() {
  const navigate = useNavigate();

  const [sku, setSKU]                     = useState({});
  const [qrDetails, setQRDetails]         = useState([]);
  const [filteredDetails, setFilteredDetails] = useState([]);
  const [searchTerm, setSearchTerm]       = useState("");
  const [batchFilter, setBatchFilter]     = useState("");
  const [selectedBatchNo, setSelectedBatchNo] = useState("");
  const [showPopUp, setShowPopUp]         = useState(false);
  const [remarks, setRemarks]             = useState("");
  const [remarksError, setRemarksError]   = useState("");

  const baseURL = sessionStorage.getItem('baseURL');

  /* ── auth + fetch ── */
  useEffect(() => {
    if (sessionStorage.getItem("isLoggedIn") !== "true") { navigate("/"); return; }
    if (sessionStorage.getItem("isAdmin") !== "true")    { navigate("/dashboard"); return; }
    const sku = JSON.parse(sessionStorage.getItem('recallBatch'));
    setSKU(sku);
    fetchDetails(sku.brand_name);
  }, [navigate]);

  const fetchDetails = async (name) => {
    const res = await axios.get(baseURL + "qr/fetchQRGenerationAnalytics", {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        });
    const filtered = res.data.filter(item => item.sku_id === name);
    setQRDetails(filtered);
  };

  /* ── QR filter change ── */
  useEffect(() => {
    if (searchTerm) {
      const match = qrDetails.find(item => item.qr_id === searchTerm);
      setFilteredDetails(match?.batch || []);
      setBatchFilter("");
    } else {
      setFilteredDetails([]);
    }
  }, [searchTerm]);

  /* ── batch no. filter ── */
  useEffect(() => {
    if (!searchTerm) return;
    const match = qrDetails.find(item => item.qr_id === searchTerm);
    if (!match) return;
    if (batchFilter) {
      setFilteredDetails(match.batch.filter(b => b.batch_no.toLowerCase().includes(batchFilter.toLowerCase())));
    } else {
      setFilteredDetails(match.batch);
    }
  }, [batchFilter]);

  /* ── recall submit ── */
  const recall = (batch_no) => {
    if (!remarks) { setRemarksError("Please enter a reason for recall."); return; }
    const fd = new FormData();
    fd.append("batch_no", batch_no);
    fd.append("remarks", remarks);
    fd.append("sku_id", sku.sku_id);
    fd.append("qr_id", searchTerm);
    axios.post(baseURL + "batch/recallBatch", fd, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
    }).then(() => {
      setShowPopUp(false);
      setRemarks("");
      fetchDetails(sku.brand_name);
    });
  };

  const formatMonthYear = (dateString) => {
    if (!dateString) return "";
    const [year, month] = dateString.split("-");
    return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long" }).format(new Date(year, month - 1));
  };

  const activeCount    = filteredDetails?.filter(b => !b.recalled).length ?? 0;
  const recalledCount  = filteredDetails?.filter(b =>  b.recalled).length ?? 0;

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div className="RecallBatch">

      {/* ── Top Bar ── */}
      <div className="dashboard-header">
        <button className="action-button" onClick={() => navigate("/dashboard")}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back</span>
        </button>
      </div>

      {/* ── Breadcrumb ── */}
      <div className="rb-breadcrumb">
        <span className="active">Recall Batch — {sku.brand_name}</span>
      </div>

      <div className="RecallBatch-container">

        {/* ── Filter Card ── */}
        <div className="rb-filter-card">
          <div className="rb-filter-card-header">
            <FontAwesomeIcon icon={faFilter} style={{ fontSize: 13, opacity: .8 }} />
            Filter Batches
          </div>
          <div className="rb-filter-body">
            <div className="rb-filter-group">
              <label>QR ID</label>
              <select value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}>
                <option value="">— Select QR ID —</option>
                {qrDetails.map((item) => (
                  <option key={item.qr_id} value={item.qr_id}>{item.qr_id}</option>
                ))}
              </select>
            </div>

            {searchTerm && (
              <div className="rb-filter-group">
                <label>Batch Number</label>
                <input
                  type="text"
                  placeholder="Filter by batch no…"
                  value={batchFilter}
                  onChange={(e) => setBatchFilter(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        {/* ── Table Card ── */}
        <div className="rb-table-card">
          <div className="rb-table-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 13, opacity: .8 }} />
              <span>
                Batch List
                {searchTerm ? ` — ${searchTerm} / ${sku.brand_name}` : ""}
              </span>
            </div>
            {searchTerm && filteredDetails?.length > 0 && (
              <div style={{ display: 'flex', gap: 10 }}>
                <span className="header-meta">{activeCount} active</span>
                {recalledCount > 0 && (
                  <span className="header-meta" style={{ color: '#FFAAAA' }}>{recalledCount} recalled</span>
                )}
              </div>
            )}
          </div>

          {!searchTerm ? (
            <div className="rb-empty-state">
              <div className="rb-empty-icon">
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <p>Select a QR ID above to view its batches.</p>
              <span>Use the filter panel to choose a QR code and optionally narrow by batch number.</span>
            </div>
          ) : filteredDetails?.length === 0 ? (
            <div className="rb-empty-state">
              <div className="rb-empty-icon">
                <FontAwesomeIcon icon={faLayerGroup} />
              </div>
              <p>No batches found{batchFilter ? ` matching "${batchFilter}"` : ""}.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="RecallBatch-table">
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th style={{ width: '22%' }}>Batch No.</th>
                    <th style={{ width: '22%' }}>Manufacturing Date</th>
                    <th style={{ width: '22%' }}>Expiry Date</th>
                    <th style={{ width: '18%' }}>Status</th>
                    <th style={{ width: '16%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetails.map((batch, index) => (
                    <tr key={index}>
                      <td style={{ color: 'var(--muted)', fontSize: 12 }}>{index + 1}</td>
                      <td>{batch.batch_no}</td>
                      <td>{formatMonthYear(batch.date_of_manufacturing)}</td>
                      <td>{formatMonthYear(batch.date_of_expiry)}</td>
                      <td>
                        {batch.recalled
                          ? <RecalledPill />
                          : <span style={{ fontSize: 12, color: 'var(--success-fg)', fontWeight: 600 }}>Active</span>
                        }
                      </td>
                      <td>
                        {batch.recalled ? (
                          <span style={{ fontSize: 12, color: 'var(--muted)' }}>—</span>
                        ) : (
                          <button
                            className="recall-btn"
                            onClick={() => { setSelectedBatchNo(batch.batch_no); setShowPopUp(true); }}
                          >
                            <FontAwesomeIcon icon={faRotateLeft} style={{ fontSize: 11 }} />
                            Recall
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Recall confirm modal ── */}
      {showPopUp && (
        <div className="popup-overlay">
          <div className="popup">
            <div className="popup-modal-header">
              <div className="popup-warning-icon">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              Confirm Batch Recall
            </div>

            <div className="popup-body">
              <p className="message">
                You are about to recall batch{" "}
                <b>{selectedBatchNo}</b> associated with QR{" "}
                <b>{searchTerm}</b> ({sku.brand_name}).
              </p>
              <div className="popup-notice">
                This action cannot be undone. The batch will be marked as recalled and all relevant parties will be notified.
              </div>

              <div className="popup-remarks-label">Reason for Recall</div>
              <textarea
                placeholder="Describe the reason for this recall…"
                rows={4}
                value={remarks}
                onChange={(e) => { setRemarks(e.target.value); setRemarksError(""); }}
              />
              {remarksError && <p className="error">{remarksError}</p>}
            </div>

            <div className="popup-footer">
              <button className="no" onClick={() => { setShowPopUp(false); setRemarks(""); setRemarksError(""); }}>
                Cancel
              </button>
              <button className="yes" onClick={() => recall(selectedBatchNo)}>
                <FontAwesomeIcon icon={faRotateLeft} style={{ marginRight: 6, fontSize: 11 }} />
                Confirm Recall
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecallBatch;